/**
 * Sprint 7a (ICD v0.2 section 9, ADR 0012, ADR 0017): battery and lift cards and the module settings on
 * the vehicle page. Shown only when the vehicle's device announced the module (or sent its data). The
 * device is authoritative: values are what it reported. Measuring and reporting only: nothing here
 * offers to stop, limit or block the vehicle (ADR 0003).
 */
import {
  BATTERY_DEFAULTS,
  BATTERY_TYPES,
  LIFT_CALIBRATION_LIMITS,
  MODULE_SETTING_LIMITS,
  chargeState,
  deepDischargeActive,
  lastFinishedCharge,
  pairChargeSessions,
  socLevel,
  type ChargeSession,
  type Vehicle,
} from "@fts/core";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { Panel } from "../components/ui";
import { hasModule, type BatteryType, type SettingKey, type VehicleModuleData, type VehicleModuleSettings } from "../data/modules";
import { useI18n, type MessageKey, type TFn } from "../i18n";
import { formatAlarmTime } from "../lib/alarms";
import { relativeTime } from "../lib/format";
import { canManageFleet } from "../lib/permissions";
import { useSession } from "../state/session";

/** How often the cards re-read the module data (ms). */
const REFRESH_MS = 60_000;

const SOC_TONE = { low: "crit", warn: "warn", ok: "ok" } as const;
const STATE_TONE = { charging: "info", discharging: "neutral", resting: "neutral", unknown: "neutral" } as const;

export function useVehicleModuleData(vehicleId: string, deviceId: string | null) {
  const { provider } = useSession();
  const [data, setData] = useState<VehicleModuleData | null>(null);
  const [error, setError] = useState(false);
  const load = useCallback(
    () =>
      provider
        .getVehicleModuleData(vehicleId)
        .then((d) => {
          setData(d);
          setError(false);
        })
        .catch(() => setError(true)),
    [provider, vehicleId],
  );
  useEffect(() => {
    if (!deviceId) {
      setData(null);
      return;
    }
    let cancelled = false;
    const run = () => void (!cancelled && load());
    run();
    const timer = setInterval(run, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [load, deviceId]);
  return { data, error, reload: load };
}

/** The battery and lift panels for the vehicle page's detail grid (nothing when no module is fitted). */
export function VehicleModulePanels({ vehicle }: { vehicle: Vehicle }) {
  const { t } = useI18n();
  const { data, error, reload } = useVehicleModuleData(vehicle.id, vehicle.deviceId);
  if (!vehicle.deviceId) return null;
  if (error && !data) {
    return (
      <Panel title={t("modules.battery.title")}>
        <p className="muted">{t("modules.loadError")}</p>
      </Panel>
    );
  }
  if (!data) return null;
  const showBattery = hasModule(data, "battery") || data.battery !== null;
  const showLift = hasModule(data, "lift") || data.lifts.length > 0;
  return (
    <>
      {showBattery && <BatteryCard data={data} nominalV={vehicle.batteryVoltage} />}
      {showLift && <LiftCard data={data} />}
      {(showBattery || showLift) && <ModuleSettingsPanel data={data} vehicle={vehicle} onSaved={reload} />}
    </>
  );
}

const fmt = (v: number | null | undefined, locale: string, digits = 1) =>
  v === null || v === undefined || !Number.isFinite(v) ? null : v.toLocaleString(locale, { maximumFractionDigits: digits, minimumFractionDigits: 0 });

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="dl-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function since(iso: string | null, locale: string, now: number): string | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  return relativeTime(Number.isFinite(at) && at > now ? new Date(now).toISOString() : iso, locale, now);
}

export function BatteryCard({ data, nominalV, now = Date.now() }: { data: VehicleModuleData; nominalV?: number | null; now?: number }) {
  const { t, locale } = useI18n();
  const dash = t("common.empty");
  const b = data.battery;
  const sessions = useMemo(() => pairChargeSessions(data.chargeEvents, new Date(now)), [data.chargeEvents, now]);
  const state = chargeState(b ? { batA: b.batA, batAt: b.at } : null, sessions, new Date(now));
  const last = lastFinishedCharge(sessions);
  const open = sessions.at(-1)?.open ? sessions.at(-1)! : null;
  // Red only when the DEVICE reported a deep discharge (the cloud knows no threshold); amber below a UI level.
  const level = socLevel(b?.soc, deepDischargeActive(data.lastDeepDischargeAt, data.chargeEvents, new Date(now)));
  // The vehicle record; the optional object form of hello.modules may also carry it.
  const nominal = nominalV ?? data.modules?.find((m) => m.type === "battery")?.nominal_v;
  const pct = (v: number | null | undefined) => (fmt(v, locale, 0) === null ? dash : t("modules.value.percent", { value: fmt(v, locale, 0)! }));
  return (
    <Panel title={t("modules.battery.title")} meta={nominal ? <span className="muted">{t("modules.battery.nominal", { value: nominal })}</span> : undefined}>
      {!b ? (
        <p className="muted">{t("modules.battery.noReading")}</p>
      ) : (
        <>
          <div className="module-soc">
            <SocMeter soc={b.soc} />
            <div className="module-soc-text">
              <strong className={`module-soc-value${level ? ` tone-${SOC_TONE[level]}` : ""}`}>{pct(b.soc)}</strong>
              <span className={`badge tone-${STATE_TONE[state]}`}>{t(`modules.state.${state}` as MessageKey)}</span>
            </div>
          </div>
          {level === "low" && state !== "charging" && (
            <p className="module-warning" role="note">
              {t("modules.battery.low")}
            </p>
          )}
          <dl className="dl">
            <Row label={t("modules.battery.voltage")}>
              <span className="mono">{fmt(b.batV, locale) === null ? dash : t("modules.value.volts", { value: fmt(b.batV, locale)! })}</span>
            </Row>
            <Row label={t("modules.battery.current")}>
              <span className="mono" title={t("modules.battery.currentHint")}>
                {/* ICD v0.2 section 9: + charge, - discharge */}
                {fmt(b.batA, locale) === null ? dash : t("modules.value.amps", { value: (b.batA! > 0 ? "+" : "") + fmt(b.batA, locale)! })}
              </span>
              <span className="muted"> · {t("modules.battery.currentHint")}</span>
            </Row>
            <Row label={t("modules.battery.temperature")}>
              <span className="mono">{fmt(b.batT, locale) === null ? dash : t("modules.value.celsius", { value: fmt(b.batT, locale)! })}</span>
            </Row>
            <Row label={t("modules.battery.lastCharge")}>
              {open ? (
                <span title={formatAlarmTime(open.startAt, locale) ?? undefined}>{t("modules.battery.chargingSince", { when: since(open.startAt, locale, now) ?? dash })}</span>
              ) : last ? (
                <LastCharge s={last} now={now} />
              ) : (
                <span className="muted">{t("modules.battery.noCharge")}</span>
              )}
            </Row>
          </dl>
          <p className="module-foot muted" title={formatAlarmTime(b.at, locale, true) ?? undefined}>
            {t("modules.battery.readingAt", { when: since(b.at, locale, now) ?? dash })}
          </p>
        </>
      )}
      <SocSparkline series={data.socSeries} now={now} />
    </Panel>
  );
}

function LastCharge({ s, now }: { s: ChargeSession; now: number }) {
  const { t, locale } = useI18n();
  const parts = [since(s.endAt, locale, now)];
  if (s.startSoc !== null && s.endSoc !== null) parts.push(t("modules.battery.socRange", { from: fmt(s.startSoc, locale, 0)!, to: fmt(s.endSoc, locale, 0)! }));
  else if (s.endSoc !== null) parts.push(t("modules.value.percent", { value: fmt(s.endSoc, locale, 0)! }));
  if (s.ah !== null) parts.push(t("modules.battery.chargedAh", { value: fmt(s.ah, locale, 0)! }));
  if (s.kwh !== null) parts.push(t("modules.battery.chargedKwh", { value: fmt(s.kwh, locale, 1)! }));
  return (
    <span title={formatAlarmTime(s.endAt, locale) ?? undefined}>
      {parts.filter(Boolean).join(" · ")}
      {s.incomplete && <span className="muted"> · {t("modules.battery.incomplete")}</span>}
    </span>
  );
}

function SocMeter({ soc }: { soc: number | null }) {
  const level = socLevel(soc);
  const w = soc === null ? 0 : Math.max(0, Math.min(100, soc));
  return (
    <span className="soc-meter" aria-hidden="true">
      <span className={`soc-meter-fill${level ? ` tone-${SOC_TONE[level]}` : ""}`} style={{ width: `${w}%` }} />
    </span>
  );
}

/** 24-hour SoC line (0-100 %), with the deep discharge threshold as a dashed reference. */
export function SocSparkline({ series, now = Date.now() }: { series: { at: string; soc: number }[]; now?: number }) {
  const { t, locale } = useI18n();
  const W = 300;
  const H = 56;
  const from = now - 24 * 3600_000;
  const pts = series
    .map((p) => ({ x: Date.parse(p.at), y: p.soc }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y) && p.x >= from - 600_000 && p.x <= now + 600_000)
    .sort((a, b) => a.x - b.x);
  if (pts.length < 2) return <p className="module-foot muted">{t("modules.battery.sparklineEmpty")}</p>;
  const sx = (x: number) => ((x - from) / (now - from)) * W;
  const sy = (y: number) => H - 2 - (Math.max(0, Math.min(100, y)) / 100) * (H - 4);
  // Break the line where readings are missing for more than 30 minutes (vehicle off, no data).
  let d = "";
  pts.forEach((p, i) => {
    const gap = i > 0 && p.x - pts[i - 1]!.x > 30 * 60_000;
    d += `${i === 0 || gap ? "M" : "L"}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`;
  });
  const min = Math.min(...pts.map((p) => p.y));
  const max = Math.max(...pts.map((p) => p.y));
  const last = pts.at(-1)!;
  const label = `${t("modules.battery.sparkline")}: ${t("modules.battery.sparklineRange", { min: fmt(min, locale, 0)!, max: fmt(max, locale, 0)! })}`;
  return (
    <figure className="soc-spark">
      <figcaption className="soc-spark-caption">
        <span>{t("modules.battery.sparkline")}</span>
        <span className="muted">{t("modules.battery.sparklineRange", { min: fmt(min, locale, 0)!, max: fmt(max, locale, 0)! })}</span>
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={label} className="soc-spark-svg">
        <line x1="0" x2={W} y1={sy(BATTERY_DEFAULTS.deepSoc)} y2={sy(BATTERY_DEFAULTS.deepSoc)} className="soc-spark-threshold" />
        <path d={d} className="soc-spark-line" />
        <circle cx={sx(last.x)} cy={sy(last.y)} r="2.5" className="soc-spark-dot" />
      </svg>
    </figure>
  );
}

export function LiftCard({ data, now = Date.now() }: { data: VehicleModuleData; now?: number }) {
  const { t, locale } = useI18n();
  const dash = t("common.empty");
  const rated = data.settings.ratedCapacityKg;
  return (
    <Panel title={t("modules.lift.title")}>
      <dl className="dl">
        <Row label={t("modules.lift.today")}>
          {t("modules.lift.todayValue", { lifts: data.liftsToday, overloads: data.overloadsToday })}
          {data.overloadsToday > 0 && <span className="badge tone-crit module-inline-badge">{t("modules.lift.overloadBadge")}</span>}
        </Row>
        <Row label={t("modules.lift.capacity")}>{rated ? t("modules.value.kg", { value: fmt(rated, locale, 0)! }) : dash}</Row>
      </dl>
      <h3 className="module-subtitle">{t("modules.lift.last")}</h3>
      {data.lifts.length === 0 ? (
        <p className="muted">{t("modules.lift.none")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table module-lifts">
            <thead>
              <tr>
                <th scope="col">{t("modules.lift.colTime")}</th>
                <th scope="col" className="num">
                  {t("modules.lift.colLoad")}
                </th>
                <th scope="col" className="num">
                  {t("modules.lift.colHeight")}
                </th>
                <th scope="col" className="num">
                  {t("modules.lift.colPressure")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.lifts.map((l) => (
                <tr key={l.id} className={l.overload ? "is-overload" : undefined}>
                  <td title={formatAlarmTime(l.at, locale, true) ?? undefined}>
                    {since(l.at, locale, now) ?? dash}
                    {l.overload && <span className="badge tone-crit module-inline-badge">{t("modules.lift.overloadBadge")}</span>}
                  </td>
                  <td className="num mono">
                    {l.loadKg === null ? (
                      <span className="muted">{t("modules.lift.noLoad")}</span>
                    ) : (
                      <>
                        {t("modules.value.kg", { value: fmt(l.loadKg, locale, 0)! })}
                        {l.estimated && <span className="muted" title={t("modules.lift.estimatedHint")}> · {t("modules.lift.estimated")}</span>}
                      </>
                    )}
                  </td>
                  <td className="num mono">{l.maxForkMm === null ? dash : t("modules.value.meters", { value: fmt(l.maxForkMm / 1000, locale, 2)! })}</td>
                  <td className="num mono">{l.peakBar === null ? dash : t("modules.value.bar", { value: fmt(l.peakBar, locale, 0)! })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="module-foot muted">{t("modules.lift.estimateNote")}</p>
    </Panel>
  );
}

const DELIVERY_TONE = { none: "neutral", kept: "warn", pending: "info", acked: "ok", failed: "crit" } as const;

/** Settings sent to the device (ICD v0.2 section 9 config) and their delivery state; fleet managers edit. */
export function ModuleSettingsPanel({ data, vehicle, onSaved }: { data: VehicleModuleData; vehicle: Vehicle; onSaved?: () => void }) {
  const { t, locale } = useI18n();
  const { role } = useSession();
  const [editing, setEditing] = useState(false);
  const dash = t("common.empty");
  const s = data.settings;
  const value = (v: number | null, key: MessageKey, digits = 1) => (v === null ? dash : t(key, { value: fmt(v, locale, digits)! }));
  return (
    <Panel
      title={t("modules.settings.title")}
      meta={
        canManageFleet(role) && (
          <button type="button" className="button button-secondary button-sm" onClick={() => setEditing(true)}>
            {t("common.edit")}
          </button>
        )
      }
    >
      {editing && (
        <ModuleSettingsDialog
          vehicle={vehicle}
          settings={s}
          locked={data.lockedSettings}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onSaved?.();
          }}
        />
      )}
      <dl className="dl">
        <Row label={t("modules.settings.batType")}>{s.batType ? t(`modules.batType.${s.batType}` as MessageKey) : dash}</Row>
        <Row label={t("modules.settings.batAh")}>{value(s.batAh, "modules.battery.chargedAh", 0)}</Row>
        <Row label={t("modules.settings.kgPerBar")}>{value(s.liftKgPerBar, "modules.value.kgPerBar", 2)}</Row>
        <Row label={t("modules.settings.threshold")}>{value(s.liftBarThreshold, "modules.value.bar", 1)}</Row>
        <Row label={t("modules.settings.delivery")}>
          <span className={`badge tone-${DELIVERY_TONE[data.delivery.state]}`}>{t(`modules.delivery.${data.delivery.state}` as MessageKey)}</span>
        </Row>
      </dl>
      <p className="module-foot muted">{t("modules.settings.note")}</p>
    </Panel>
  );
}

type Limits = { min: number; max: number };
function parseField(raw: string, lim: Limits, t: TFn, integer = false): { value: number | null; error: string | null } {
  const s = raw.trim().replace(",", ".");
  if (!s) return { value: null, error: null };
  const n = Number(s);
  if (!Number.isFinite(n) || n < lim.min || n > lim.max || (integer && !Number.isInteger(n))) return { value: null, error: t("modules.settings.range", { min: lim.min, max: lim.max }) };
  return { value: n, error: null };
}

const str = (v: number | null) => (v === null ? "" : String(v));

export function ModuleSettingsDialog({
  vehicle,
  settings,
  locked = [],
  onClose,
  onSaved,
}: {
  vehicle: Vehicle;
  settings: VehicleModuleSettings;
  /** Already sent to the device: may be changed, not cleared (the device keeps its last value). */
  locked?: SettingKey[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const { provider } = useSession();
  const [batType, setBatType] = useState<BatteryType | "">(settings.batType ?? "");
  const [batAh, setBatAh] = useState(str(settings.batAh));
  const [kgPerBar, setKgPerBar] = useState(str(settings.liftKgPerBar));
  const [threshold, setThreshold] = useState(str(settings.liftBarThreshold));
  const [emptyBar, setEmptyBar] = useState(str(settings.liftEmptyBar));
  const [rated, setRated] = useState(str(settings.ratedCapacityKg));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const lockedMsg = t("modules.settings.locked");
  const required = (key: SettingKey, raw: string, r: { value: number | null; error: string | null }) =>
    locked.includes(key) && !raw.trim() ? { value: null, error: lockedMsg } : r;
  const f = {
    batType: { value: null, error: locked.includes("batType") && !batType ? lockedMsg : null },
    batAh: required("batAh", batAh, parseField(batAh, MODULE_SETTING_LIMITS.batAh, t, true)),
    kgPerBar: required("liftKgPerBar", kgPerBar, parseField(kgPerBar, MODULE_SETTING_LIMITS.liftKgPerBar, t)),
    threshold: required("liftBarThreshold", threshold, parseField(threshold, MODULE_SETTING_LIMITS.liftBarThreshold, t)),
    emptyBar: parseField(emptyBar, LIFT_CALIBRATION_LIMITS.emptyBar, t),
    rated: parseField(rated, LIFT_CALIBRATION_LIMITS.ratedCapacityKg, t, true),
  };
  const shown = (key: keyof typeof f) => (touched ? f[key].error : null);

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    setTouched(true);
    if (Object.values(f).some((x) => x.error)) return;
    setBusy(true);
    setError(null);
    try {
      await provider.saveModuleSettings({
        vehicleId: vehicle.id,
        batType: batType || null,
        batAh: f.batAh.value,
        liftKgPerBar: f.kgPerBar.value,
        liftBarThreshold: f.threshold.value,
        liftEmptyBar: f.emptyBar.value,
        ratedCapacityKg: f.rated.value,
      });
      onSaved();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  const lockKey: Partial<Record<keyof typeof f, SettingKey>> = { batAh: "batAh", kgPerBar: "liftKgPerBar", threshold: "liftBarThreshold" };
  const num = (id: string, key: keyof typeof f, label: string, val: string, set: (v: string) => void, hint?: string, mode: "decimal" | "numeric" = "decimal") => (
    <FormField
      id={id}
      label={label}
      optional={!(lockKey[key] && locked.includes(lockKey[key]!))}
      hint={lockKey[key] && locked.includes(lockKey[key]!) ? [hint, lockedMsg].filter(Boolean).join(" ") : hint}
      error={shown(key)}
    >
      <input inputMode={mode} value={val} onChange={(x) => set(x.target.value)} {...fieldAria(id, { hint, error: shown(key) })} />
    </FormField>
  );

  return (
    <Dialog
      title={t("modules.settings.title")}
      description={t("modules.settings.description")}
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("common.save")}
          </button>
        </>
      }
    >
      {error !== null && <FormError error={error} />}
      <FormField
        id="ms-bat-type"
        label={t("modules.settings.batType")}
        optional={!locked.includes("batType")}
        hint={locked.includes("batType") ? lockedMsg : undefined}
        error={shown("batType")}
      >
        <select id="ms-bat-type" value={batType} onChange={(x) => setBatType(x.target.value as BatteryType | "")}>
          {!locked.includes("batType") && <option value="">{t("form.select")}</option>}
          {BATTERY_TYPES.map((b) => (
            <option key={b} value={b}>
              {t(`modules.batType.${b}` as MessageKey)}
            </option>
          ))}
        </select>
      </FormField>
      {num("ms-bat-ah", "batAh", t("modules.settings.batAhLabel"), batAh, setBatAh, undefined, "numeric")}
      {num("ms-kpb", "kgPerBar", t("modules.settings.kgPerBarLabel"), kgPerBar, setKgPerBar, t("modules.settings.kgPerBarHint"))}
      {num("ms-threshold", "threshold", t("modules.settings.thresholdLabel"), threshold, setThreshold, t("modules.settings.thresholdHint"))}
      {num("ms-empty", "emptyBar", t("modules.settings.emptyBarLabel"), emptyBar, setEmptyBar, t("modules.settings.emptyBarHint"))}
      {num("ms-rated", "rated", t("modules.settings.ratedLabel"), rated, setRated, undefined, "numeric")}
    </Dialog>
  );
}
